import { useTranslation } from '@obvio/app'
import { serverClient } from '@obvio/server'

import { schema } from '@/api/schema'
import { ArticleSelect } from '@/components/Blog/BlogIndex/BlogIndex'
import { BlogIndexArticles } from '@/components/Blog/BlogIndex/BlogIndexArticles'
import { BlogPagination } from '@/components/Blog/BlogPagination'
import {
  BLOG_PAGE_SIZE,
  blogPageCount,
  blogPagePath,
} from '@/components/Blog/consts'
import { normalizeArticle } from '@/components/Blog/normalizeArticle'
import { Breadcrumbs } from '@/components/Breadcrumbs'
import { Layout } from '@/components/Layout'
import { PageSeo } from '@/components/PageSeo'
import {
  CenteredSection,
  MaxWidthContent,
} from '@/components/Renderer/CenteredSection'
import { Heading } from '@/components/Renderer/RendererVariants/Heading'
import { HeroSection } from '@/components/Renderer/RendererVariants/HeroSection'
import { REVALIDATE_SECONDS } from '@/constants/revalidate'
import { SITE_NAME } from '@/constants/seo'

import type { ArticleCategory } from '@/components/Blog/BlogIndex/BlogIndex'
import type {
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from 'next'
import type { ReactElement } from 'react'

type PageProps = {
  articles: ArticleCategory['articles']
  page: number
  pages: number
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  const client = await serverClient(schema, { isAdmin: true })
  const articles = await client.query('getArticles', { select: { id: true } })

  return {
    paths: Array.from({ length: blogPageCount(articles.length) }, (_, i) => ({
      params: { page: String(i + 1) },
    })),
    fallback: 'blocking',
  }
}

export async function getStaticProps(
  ctx: GetStaticPropsContext<{ page: string }>,
): Promise<GetStaticPropsResult<PageProps>> {
  const page = Number(ctx.params?.page)
  if (!Number.isInteger(page) || page < 1) {
    return { notFound: true }
  }

  const client = await serverClient(schema, { ...ctx, isAdmin: true })
  const all = await client.query('getArticles', { select: ArticleSelect })

  const pages = blogPageCount(all.length)
  if (page > pages) {
    return { notFound: true }
  }

  const articles = all
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
    .slice((page - 1) * BLOG_PAGE_SIZE, page * BLOG_PAGE_SIZE)
    .map(normalizeArticle)

  return {
    ...client.ssrData({ articles, page, pages }),
    revalidate: REVALIDATE_SECONDS,
  }
}

function Page({ articles, page, pages }: PageProps): ReactElement {
  const { t } = useTranslation()
  const label = t('blog.page', { page })

  return (
    <>
      <PageSeo
        path={blogPagePath(page)}
        title={`${label} - ${SITE_NAME}`}
        description={t('blog.archiveDescription', { page })}
      />
      <Breadcrumbs
        trail={[
          { name: 'Blog', path: 'blog' },
          { name: label, path: blogPagePath(page) },
        ]}
      />
      <Layout type="Główny">
        <HeroSection heading img="/static/img/blog_tło.jpeg" text="BLOG" />
        <Heading heading={label.toUpperCase()} />
        <CenteredSection>
          <MaxWidthContent>
            <BlogIndexArticles articles={articles} />
          </MaxWidthContent>
        </CenteredSection>
        <CenteredSection>
          <BlogPagination total={pages} current={page} />
        </CenteredSection>
      </Layout>
    </>
  )
}

export default Page
